/**
 * Time handling for the astronomy engine.
 *
 * All times are stored as UTC milliseconds since the Unix epoch. Julian Date
 * and sidereal time are DERIVED quantities, never stored as authoritative
 * inputs, so there is a single source of truth.
 *
 * Algorithms:
 * - Julian Date (UT): standard formula via astronomy-engine's AstroTime,
 *   which implements the well-established relationship
 *   JD(UT) = unix/86400000 + 2440587.5.
 * - Julian Date (TT): astronomy-engine applies its DeltaT model
 *   (Espenak & Meeus 2006 polynomial), documented in the library source
 *   (function DeltaT_EspenakMeeus).
 * - Greenwich sidereal time: astronomy-engine's SiderealTime() returns
 *   Greenwich APPARENT Sidereal Time (GAST), computed from the IAU 2006
 *   Earth rotation angle plus the equation of the equinoxes. GAST is the
 *   correct rotation angle for of-date equatorial coordinates, which is
 *   exactly how the engine consumes it.
 */

import { AstroTime, MakeTime, SiderealTime } from "astronomy-engine";

import type { ObserverTime } from "./types";

const MILLIS_PER_DAY = 86_400_000;
const JULIAN_DATE_UNIX_EPOCH = 2_440_587.5;

/** True Julian Date (UT1 ≈ UTC) at the Unix epoch. */
export const JULIAN_DATE_UNIX_EPOCH_UT = JULIAN_DATE_UNIX_EPOCH;

/** Julian Date (TT) of the J2000.0 epoch, 2000-01-01 12:00 TT. */
export const JULIAN_DATE_J2000_TT = 2_451_545.0;

/** Converts a UTC Date to a Julian Date (UT). */
export function julianDateUt(date: Date): number {
  return date.getTime() / MILLIS_PER_DAY + JULIAN_DATE_UNIX_EPOCH;
}

/** Converts a Julian Date (UT) to a UTC Date. */
export function dateFromJulianDateUt(julianDay: number): Date {
  return new Date((julianDay - JULIAN_DATE_UNIX_EPOCH) * MILLIS_PER_DAY);
}

/** 0-argument identity "DeltaT" used to pin TT = UT when requested. */
export interface DeltaTPolicy {
  /** Compute TT - UT in seconds. */
  deltaTSeconds(date: Date): number;
}

/** astronomy-engine's Espenak & Meeus DeltaT model (default policy). */
export class EspenakMeeusDeltaT implements DeltaTPolicy {
  deltaTSeconds(date: Date): number {
    const time = MakeTime(date);
    const diff = time.tt - time.ut;
    return diff * 86_400;
  }
}

/**
 * Builds the canonical ObserverTime for a UTC instant.
 * All derived quantities (JD, sidereal time) come from astronomy-engine's
 * well-tested AstroTime implementation.
 */
export function observerTimeFromUtc(utcMilliseconds: number): ObserverTime {
  const time = MakeTime(new Date(utcMilliseconds));
  return {
    utcMilliseconds,
    julianDayUt: time.ut,
    julianDayTt: time.tt,
    siderealHours: SiderealTime(time),
  };
}

/** Builds an ObserverTime from a UTC Date. */
export function observerTimeFromDate(date: Date): ObserverTime {
  return observerTimeFromUtc(date.getTime());
}

/** Local mean sidereal time (LMST) for an observer, in hours [0, 24). */
export function localSiderealHours(observerTime: ObserverTime, longitudeDegrees: number): number {
  const hours = observerTime.siderealHours + longitudeDegrees / 15;
  return ((hours % 24) + 24) % 24;
}

/** The current instant, as an ObserverTime. */
export function nowObserverTime(): ObserverTime {
  return observerTimeFromUtc(Date.now());
}

export { AstroTime };
